import * as i0 from '@angular/core';
import { Injectable, Optional, Inject, Directive, Input, Output, Component, ChangeDetectionStrategy, HostListener, Pipe, NgModule } from '@angular/core';
import { from, Subject } from 'rxjs';
import { init, PickerDisplayMode } from 'filestack-js';
import * as i1 from '@angular/common';
import { CommonModule } from '@angular/common';

class FilestackService {
    constructor(config) {
        this.config = config;
        if (!config) {
            return;
        }
        this.clientOptions = config.options;
        this.apikey = config.apikey;
    }
    get client() {
        if (!this.clientInstance) {
            return this.init();
        }
        return this.clientInstance;
    }
    set client(client) {
        this.clientInstance = client;
    }
    /**
     * Set another client instance as this one used by FilestackService
     * @param client - An instance of filestack client class
     */
    setClientInstance(client) {
        if (client) {
            this.client = client;
        }
    }
    /**
     * Initialize filestack client
     * @param apikey - Filestack apikey
     * @param clientOptions - Client options
     */
    init(apikey, clientOptions) {
        this.client = init(apikey || this.apikey, clientOptions || this.clientOptions);
        return this.client;
    }
    /**
     * Instance of picker class
     * @param options - picker options
     */
    picker(options) {
        return this.client.picker(options);
    }
    /**
     * Generate url with filestack transformations
     * @param url - Filestack handle or external url
     * @param options - Transformation options
     * @param b64 - Encode url params in base64
     */
    transform(url, options, b64) {
        return this.client.transform(url, options, b64);
    }
    /**
     * Access files via their Filestack handles
     * @param handle - Filestack handle
     * @param options - Retrieve options
     * @param security - Filestack security object
     */
    retrieve(handle, options, security) {
        return from(this.client.retrieve(handle, options, security));
    }
    /**
     * Retrieve detailed data of stored files
     * @param handle - Filestack handle
     * @param options - Metadata options
     * @param security - Filestack security object
     */
    metadata(handle, options, security) {
        return from(this.client.metadata(handle, options, security));
    }
    /**
     * Store a file from its URL
     * @param url - Valid url to file
     * @param options - Store params
     * @param token - Optional control token to call .cancel()
     * @param security - Filestack security object
     */
    storeURL(url, options, token, security) {
        return from(this.client.storeURL(url, options, token, security));
    }
    /**
     * Upload a provided file
     * @param file - A file or array of files to upload
     * @param options - Upload options
     * @param storeOptions - Store options
     * @param token - Optional control token to call .cancel()
     * @param security - Filestack security object
     */
    upload(file, options, storeOptions, token, security) {
        if (Array.isArray(file)) {
            return from(this.client.multiupload(file, options, storeOptions, token, security));
        }
        return from(this.client.upload(file, options, storeOptions, token, security));
    }
    /**
     * Remove a file from storage and the Filestack system
     * @param handle - Filestack handle
     * @param security - Filestack security object
     */
    remove(handle, security) {
        return from(this.client.remove(handle, security));
    }
    /**
     * Remove a file only from the Filestack system. The file remains in storage
     * @param handle - Filestack handle
     * @param security - Filestack security object
     */
    removeMetadata(handle, security) {
        return from(this.client.removeMetadata(handle, security));
    }
    /**
     * Used for viewing files via Filestack handles or storage aliases
     * @param handle - Filestack handle
     * @param options - Preview options
     */
    preview(handle, options) {
        return this.client.preview(handle, options);
    }
    /**
     * Clear all current cloud sessions in the picker or
     * particular one if name is passed
     * @param name - Optional cloud name to be passed
     */
    logout(name) {
        return from(this.client.logout(name));
    }
}
FilestackService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: FilestackService, deps: [{ token: 'config', optional: true }], target: i0.ɵɵFactoryTarget.Injectable });
FilestackService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: FilestackService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: FilestackService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: ['config']
                }] }]; } });

class PickerBaseDirective {
    constructor(filestackService) {
        this.filestackService = filestackService;
        this.elementId = 'picker-container';
        this.uploadSuccess = new Subject();
        this.uploadError = new Subject();
        this.generateId();
    }
    ngOnInit() {
        this.filestackService.init(this.apikey, this.clientOptions);
    }
    generateId() {
        this.elementId = `picker-container-${Date.now()}`;
    }
    ngOnDestroy() {
        if (this.picker) {
            this.picker.close();
        }
    }
}
PickerBaseDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: PickerBaseDirective, deps: [{ token: FilestackService }], target: i0.ɵɵFactoryTarget.Directive });
PickerBaseDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.3.0", type: PickerBaseDirective, inputs: { apikey: "apikey", pickerOptions: "pickerOptions", clientOptions: "clientOptions", file: "file", source: "source" }, outputs: { uploadSuccess: "uploadSuccess", uploadError: "uploadError" }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: PickerBaseDirective, decorators: [{
            type: Directive,
            args: [{}]
        }], ctorParameters: function () { return [{ type: FilestackService, decorators: [{
                    type: Inject,
                    args: [FilestackService]
                }] }]; }, propDecorators: { apikey: [{
                type: Input
            }], pickerOptions: [{
                type: Input
            }], clientOptions: [{
                type: Input
            }], file: [{
                type: Input
            }], source: [{
                type: Input
            }], uploadSuccess: [{
                type: Output
            }], uploadError: [{
                type: Output
            }] } });

class PickerOverlayComponent extends PickerBaseDirective {
    constructor() {
        super(...arguments);
        this.isActive = false;
    }
    ngAfterContentInit() {
        // Overwrite display mode to be always 'overlay' in this component
        this.picker = this.filestackService.picker({
            ...this.pickerOptions,
            rootId: `picker-overlay-${Date.now()}`,
            container: this.elementId,
            displayMode: PickerDisplayMode.overlay,
            onUploadDone: res => this.uploadSuccess.next(res),
            onClose: () => {
                this.isActive = false;
                this.generateId();
            }
        });
        console.log({
            ...this.pickerOptions,
            container: `#${this.elementId}`,
            displayMode: PickerDisplayMode.overlay,
            onUploadDone: res => this.uploadSuccess.next(res),
            onClose: () => {
                this.isActive = false;
                this.generateId();
            }
        });
    }
    onClick(event) {
        if (this.isActive) {
            return;
        }
        event.stopPropagation();
        event.preventDefault();
        this.isActive = true;
        // Picker open success handler there is ommited, because it's accessible from pickerOptions
        this.picker.open().catch(err => this.uploadError.next(err));
    }
}
PickerOverlayComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: PickerOverlayComponent, deps: null, target: i0.ɵɵFactoryTarget.Component });
PickerOverlayComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: PickerOverlayComponent, selector: "ng-picker-overlay", host: { listeners: { "click": "onClick($event)" } }, usesInheritance: true, ngImport: i0, template: '<div><ng-content class="ng-picker"></ng-content><div *ngIf="isActive" [id]="elementId"></div></div>', isInline: true, dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: PickerOverlayComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'ng-picker-overlay',
                    template: '<div><ng-content class="ng-picker"></ng-content><div *ngIf="isActive" [id]="elementId"></div></div>',
                    changeDetection: ChangeDetectionStrategy.OnPush
                }]
        }], propDecorators: { onClick: [{
                type: HostListener,
                args: ['click', ['$event']]
            }] } });

class PickerDropPaneComponent extends PickerBaseDirective {
    ngAfterContentInit() {
        // Overwrite display mode to be always 'dropPane' in this component
        this.picker = this.filestackService.picker({
            ...this.pickerOptions,
            rootId: `picker-overlay-${Date.now()}`,
            displayMode: PickerDisplayMode.dropPane,
            container: this.elementId,
            onUploadDone: res => this.uploadSuccess.next(res)
        });
        // Picker open success handler there is ommited, because it's accessible from pickerOptions
        this.picker.open()
            .then(void (0))
            .catch(err => this.uploadError.next(err));
    }
}
PickerDropPaneComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: PickerDropPaneComponent, deps: null, target: i0.ɵɵFactoryTarget.Component });
PickerDropPaneComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: PickerDropPaneComponent, selector: "ng-picker-drop-pane", usesInheritance: true, ngImport: i0, template: '<div [id]="elementId" class="ng-picker"></div>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: PickerDropPaneComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'ng-picker-drop-pane',
                    template: '<div [id]="elementId" class="ng-picker"></div>',
                    changeDetection: ChangeDetectionStrategy.OnPush
                }]
        }] });

class PickerInlineComponent extends PickerBaseDirective {
    ngAfterContentInit() {
        // Overwrite display mode to be always 'inline' in this component
        this.picker = this.filestackService.picker({
            ...this.pickerOptions,
            rootId: `picker-overlay-${Date.now()}`,
            displayMode: PickerDisplayMode.inline,
            container: this.elementId,
            onUploadDone: res => this.uploadSuccess.next(res)
        });
        // Picker open success handler there is ommited, because it's accessible from pickerOptions
        this.picker.open()
            .then(void (0))
            .catch(err => this.uploadError.next(err));
    }
}
PickerInlineComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: PickerInlineComponent, deps: null, target: i0.ɵɵFactoryTarget.Component });
PickerInlineComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: PickerInlineComponent, selector: "ng-picker-inline", usesInheritance: true, ngImport: i0, template: '<div [id]="elementId" class="ng-picker"></div>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: PickerInlineComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'ng-picker-inline',
                    template: '<div [id]="elementId" class="ng-picker"></div>',
                    changeDetection: ChangeDetectionStrategy.OnPush
                }]
        }] });

class FilestackTransformPipe {
    constructor(filestackService) {
        this.filestackService = filestackService;
    }
    transform(value, transformOptions) {
        return this.filestackService.transform(value, transformOptions);
    }
}
FilestackTransformPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: FilestackTransformPipe, deps: [{ token: FilestackService }], target: i0.ɵɵFactoryTarget.Pipe });
FilestackTransformPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: FilestackTransformPipe, name: "filestackTransform" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: FilestackTransformPipe, decorators: [{
            type: Pipe,
            args: [{ name: 'filestackTransform' }]
        }], ctorParameters: function () { return [{ type: FilestackService }]; } });

class FilestackModule {
    static forRoot(config) {
        return {
            ngModule: FilestackModule,
            providers: [FilestackService, { provide: 'config', useValue: config }]
        };
    }
}
FilestackModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: FilestackModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
FilestackModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: FilestackModule, declarations: [PickerOverlayComponent,
        PickerInlineComponent,
        PickerDropPaneComponent,
        FilestackTransformPipe], imports: [CommonModule], exports: [PickerOverlayComponent,
        PickerInlineComponent,
        PickerDropPaneComponent,
        FilestackTransformPipe] });
FilestackModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: FilestackModule, providers: [
        FilestackService
    ], imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: FilestackModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                    ],
                    providers: [
                        FilestackService
                    ],
                    declarations: [
                        PickerOverlayComponent,
                        PickerInlineComponent,
                        PickerDropPaneComponent,
                        FilestackTransformPipe
                    ],
                    exports: [
                        PickerOverlayComponent,
                        PickerInlineComponent,
                        PickerDropPaneComponent,
                        FilestackTransformPipe
                    ]
                }]
        }] });

/*
 * Public API Surface of filestack-angular
 */

/**
 * Generated bundle index. Do not edit.
 */

export { FilestackModule, FilestackService, FilestackTransformPipe, PickerDropPaneComponent, PickerInlineComponent, PickerOverlayComponent };
